import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { HeartIcon } from "../ui";
import { getUser, logoutUser, type AuthUser } from "../../utils/auth";

const links = [
  { href: "/#how-it-works", label: "How it works" },
  { href: "/#features", label: "Features" },
  { href: "/#get-started", label: "Get started" },
  { href: "/#pricing", label: "Pricing" },
  { href: "/#faq", label: "FAQ" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [user, setUser] = useState<AuthUser | null>(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  // Sync auth state from localStorage on mount + on authChange event
  useEffect(() => {
    const syncUser = () => setUser(getUser());
    syncUser();
    window.addEventListener("authChange", syncUser);
    window.addEventListener("storage", syncUser);
    return () => {
      window.removeEventListener("authChange", syncUser);
      window.removeEventListener("storage", syncUser);
    };
  }, []);

  // Scroll handler
  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", handler, { passive: true });
    return () => window.removeEventListener("scroll", handler);
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node)
      ) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  function handleLogout() {
    logoutUser();
    setDropdownOpen(false);
    setOpen(false);
    navigate("/");
  }

  function initials(name?: string, email?: string) {
    if (name && name.trim()) {
      const parts = name.trim().split(/\s+/);
      return (parts[0][0] + (parts[1]?.[0] || "")).toUpperCase();
    }
    if (email) return email[0].toUpperCase();
    return "U";
  }

  function getAvatarUrl(u: AuthUser | null) {
    if (u?.photos && u.photos.length > 0) {
      return u.photos[0].url;
    }
    return null;
  }

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 flex items-center justify-between px-[5%] transition-all duration-300 ${
        scrolled
          ? "py-3 bg-white/95 backdrop-blur-xl shadow-sm border-b border-black/5"
          : "py-5 bg-white/92 backdrop-blur-md border-b border-black/5"
      }`}
    >
      {/* Logo */}
      <a
        href="/"
        className="flex items-center gap-2.5 font-bold text-[17px] tracking-tight text-black no-underline"
      >
        <div className="w-9 h-9 rounded-[10px] bg-black flex items-center justify-center flex-shrink-0">
          <HeartIcon className="w-[18px] h-[18px] text-white" />
        </div>
        Max &amp; Sherry
      </a>

      {/* Desktop links */}
      <ul className="hidden md:flex items-center gap-7 list-none">
        {links.map(({ href, label }) => (
          <li key={href}>
            <Link
              to={href}
              className="text-sm font-normal text-muted hover:text-black transition-colors duration-200 relative group no-underline"
            >
              {label}
              <span className="absolute -bottom-0.5 left-0 w-0 h-[1.5px] bg-black group-hover:w-full transition-all duration-300" />
            </Link>
          </li>
        ))}

        {/* Login OR User avatar */}
        <li>
          {user ? (
            <div className="relative" ref={dropdownRef}>
              <button
                type="button"
                onClick={() => setDropdownOpen((v) => !v)}
                className="flex items-center gap-2 focus:outline-none"
              >
                <div className="w-9 h-9 rounded-full bg-rose text-white text-[12px] font-bold flex items-center justify-center hover:opacity-90 transition-opacity overflow-hidden relative flex-shrink-0">
                  {getAvatarUrl(user) ? (
                    <img
                      src={getAvatarUrl(user)!}
                      alt={user.name || "Profile"}
                      className="absolute inset-0 w-full h-full object-cover"
                    />
                  ) : (
                    initials(user.name, user.email)
                  )}
                </div>
                <svg
                  className={`w-3 h-3 text-muted transition-transform ${
                    dropdownOpen ? "rotate-180" : ""
                  }`}
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                >
                  <polyline points="6,9 12,15 18,9" />
                </svg>
              </button>

              {dropdownOpen && (
                <div className="absolute right-0 mt-3 w-64 bg-white rounded-2xl border border-black/10 shadow-lg overflow-hidden">
                  <div className="px-4 py-4 border-b border-black/5">
                    <p className="text-sm font-semibold text-black truncate">
                      {user.name || "User"}
                    </p>
                    <p className="text-[12px] text-muted truncate">
                      {user.email || user.phone || ""}
                    </p>
                  </div>
                  <div className="py-1">
                    <Link
                      to="/account"
                      onClick={() => setDropdownOpen(false)}
                      className="block px-4 py-2.5 text-[13px] text-black hover:bg-neutral-50 no-underline font-medium"
                    >
                      My account
                    </Link>
                    <Link
                      to="/my-chats"
                      onClick={() => setDropdownOpen(false)}
                      className="block px-4 py-2.5 text-[13px] text-black hover:bg-neutral-50 no-underline"
                    >
                      My Chats
                    </Link>
                    <Link
                      to="/leads"
                      onClick={() => setDropdownOpen(false)}
                      className="block px-4 py-2.5 text-[13px] text-black hover:bg-neutral-50 no-underline"
                    >
                      My leads
                    </Link>
                    <Link
                      to="/pay"
                      onClick={() => setDropdownOpen(false)}
                      className="block px-4 py-2.5 text-[13px] text-black hover:bg-neutral-50 no-underline"
                    >
                      Upgrade plan
                    </Link>
                  </div>
                  <div className="border-t border-black/5 py-1">
                    <button
                      type="button"
                      onClick={handleLogout}
                      className="w-full text-left px-4 py-2.5 text-[13px] text-rose-dark hover:bg-neutral-50"
                    >
                      Log out
                    </button>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <Link
              to="/pay?returnTo=/account"
              className="text-sm font-semibold text-magenta hover:text-rose-dark transition-colors duration-200 no-underline"
            >
              Log in
            </Link>
          )}
        </li>

        <li>
          <Link
            to="/#download"
            className="bg-black text-white text-sm font-medium px-5 py-2.5 rounded-full hover:bg-neutral-800 transition-colors duration-200 no-underline"
          >
            Get the app
          </Link>
        </li>
      </ul>

      {/* Mobile hamburger */}
      <button
        onClick={() => setOpen(!open)}
        className="md:hidden w-8 h-8 flex flex-col justify-center gap-1.5"
      >
        <span
          className={`block h-0.5 bg-black transition-all duration-300 ${open ? "rotate-45 translate-y-2" : ""}`}
        />
        <span
          className={`block h-0.5 bg-black transition-all duration-300 ${open ? "opacity-0" : ""}`}
        />
        <span
          className={`block h-0.5 bg-black transition-all duration-300 ${open ? "-rotate-45 -translate-y-2" : ""}`}
        />
      </button>

      {/* Mobile menu */}
      {open && (
        <div className="absolute top-full  left-1/2 -translate-x-1/2 mt-3 w-[80%] max-w-xs bg-white/95 backdrop-blur-md rounded-2xl border border-black/5 shadow-lg md:hidden p-5 flex flex-col items-center text-center gap-3">
          {links.map(({ href, label }) => (
            <Link
              key={href}
              to={href}
              onClick={() => setOpen(false)}
              className="text-sm font-bold text-black no-underline py-2"
            >
              {label}
            </Link>
          ))}

          {user ? (
            <>
              <div className="flex items-center justify-center gap-3 py-2 border-t border-black/5 pt-3 w-full">
                <div className="w-10 h-10 rounded-full bg-rose text-white text-sm font-bold flex items-center justify-center overflow-hidden relative flex-shrink-0">
                  {getAvatarUrl(user) ? (
                    <img
                      src={getAvatarUrl(user)!}
                      alt={user.name || "Profile"}
                      className="absolute inset-0 w-full h-full object-cover"
                    />
                  ) : (
                    initials(user.name, user.email)
                  )}
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-black truncate">
                    {user.name || "User"}
                  </p>
                  <p className="text-[12px] text-muted truncate">
                    {user.email || user.phone || ""}
                  </p>
                </div>
              </div>
              <Link
                to="/account"
                onClick={() => setOpen(false)}
                className="text-sm font-bold text-black no-underline py-2"
              >
                My account
              </Link>
              <Link
                to="/my-chats"
                onClick={() => setOpen(false)}
                className="text-sm font-bold text-black no-underline py-2"
              >
                My Chats
              </Link>
              <Link
                to="/leads"
                onClick={() => setOpen(false)}
                className="text-sm font-bold text-black no-underline py-2"
              >
                My leads
              </Link>
              <button
                type="button"
                onClick={handleLogout}
                className="text-left text-sm font-medium text-rose-dark no-underline py-2"
              >
                Log out
              </button>
            </>
          ) : (
            <Link
              to="/pay?returnTo=/account"
              onClick={() => setOpen(false)}
              className="bg-magenta text-white text-sm font-bold px-10 py-2.5 rounded-xl text-center no-underline mt-2 shadow-sm"
            >
              Log in
            </Link>
          )}

          <Link
            to="/#download"
            onClick={() => setOpen(false)}
            className="bg-black text-white text-sm font-medium px-10 py-2.5 rounded-full text-center no-underline mt-2"
          >
            Get the app
          </Link>
        </div>
      )}
    </nav>
  );
}
